/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { createWineClubUpsellInput, wineClubUpsellAction } from '../../dataActions/wine-club-upsell';
import { IWineClubOffersBusData, WineClubOffersBusInput } from './../../dataActions/wine-club-offers-bus';
import { IOfferOptionContainerData } from './offer-option-container.data';
import { IOfferOptionContainerProps } from './offer-option-container.props.autogenerated';
/**
 *
 * OfferOptionContainer component
 * @extends {React.Component<IOfferOptionContainerProps<IOfferOptionContainerData>>}
 */
@observer
class OfferOptionContainer extends React.Component<IOfferOptionContainerProps<IOfferOptionContainerData>> {

    @observable private _showError: { show: boolean; error: string } = {
        show: false,
        error: ''
    };

    constructor(props: IOfferOptionContainerProps<IOfferOptionContainerData>) {
        super(props);

        this._takeOffer = this._takeOffer.bind(this);
    }

    public render(): JSX.Element | null {
        const {
            offerTaken
        } = this.props.data.offerOptionBus as unknown as IWineClubOffersBusData;

        const { className, buttonText } = this.props.config;
        return (
            <section className={`${className || ''} offer-option-container`}>
                <div className='offer-option-container-items'>
                    {this.props.slots.modules && this._renderSlotItems(this.props.slots.modules)}
                </div>
                {this._offerAcceptedText}
                {this._showError.show && <div className='offer-option-container-error'>{this._showError.error}</div>}
                <button onClick={this._takeOffer} disabled={offerTaken}>{buttonText}</button>
            </section>
        );
    }

    private _renderSlotItems(items: React.ReactNode[]): JSX.Element {
        return (
            <React.Fragment>
                {items.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    private get _offerAcceptedText(): JSX.Element | null {
        const {
            offerTaken,
            offerIdUsed,
            offerAcceptedText
        } = this.props.data.offerOptionBus as unknown as IWineClubOffersBusData;
        let text: RichText | undefined;
        if (offerTaken && offerIdUsed === this.props.config.offerId) {
            text = offerAcceptedText;
        }

        if (text) {
            return (
                <RichTextComponent text={text} className='offer-accepted-text' />
            );
        }

        return null;
    }

    private _takeOffer(): void {
        const input = createWineClubUpsellInput({
            customerId: this.props.context.request.user.customerAccountNumber || '',
            offerId: this.props.config.offerId,
            wineClubId: (this.props.data.offerOptionBus as unknown as IWineClubOffersBusData).wineClubId
        });

        const {
            offerOptionContainer__alreadyAccepted,
            offerOptionContainer__genericError,
            offerOptionContainer__offerExpired,
            offerOptionContainer__customerNotClubMember
        } = this.props.resources;

        wineClubUpsellAction(input, this.props.context.actionContext)
            // the result type needs to be redefined when the proxy has it's update
            // to { Status: string, Result: WineClubUpsellResponse }
            // tslint:disable-next-line: no-any
            .then((result: any) => {

                result.Status = result.result;
                // any status that isn't OFFER_ACCEPTED will be treated as an
                // error occuring. OK is the default 200 status
                if (result.Status !== 'SUCCESS') {
                    const codes = {
                        ALREADY_ACCEPTED: offerOptionContainer__alreadyAccepted,
                        OFFER_EXPIRED: offerOptionContainer__offerExpired,
                        CUSTOMER_NOT_CLUBMEMBER: offerOptionContainer__customerNotClubMember
                    };
                    if (codes[result.Status]) {
                        this._showError = {
                            show: true,
                            error: codes[result.Status]
                        };
                    } else {
                        this._showError = {
                            show: true,
                            error: offerOptionContainer__genericError
                        };
                    }
                } else {
                    // if Status is actually OFFER_ACCEPTED
                    this._showError = { show: false, error: '' };
                    const addedState = { offerTaken: true, offerIdUsed: this.props.config.offerId };
                    this.props.context.actionContext.update(new WineClubOffersBusInput(), { ...this.props.data.offerOptionBus, ...addedState });
                }

            })
            .catch(err => {
                console.error(err);
                this._showError = { show: true, error: offerOptionContainer__genericError };
            });
    }
}

export default OfferOptionContainer;
